import { race, call, take, takeEvery, select, fork } from 'redux-saga/effects';
import { logoutSuccess, authSuccess, loginSuccess, loginRejected, authRejected, logoutRejected } from './actions';
import selectors from './selectors';
import { getLogger } from '../../logger/index';

const logger = getLogger('communicationVideo :: sagaUtils');

const loginAsEmployeeAction = ({ type, payload }) =>
  ['HANDLE_LOGIN_FULFILLED', 'FETCH_CREDENTIALS_FULFILLED'].includes(type) && payload.data.type === 'employee';
const loginAsCustomerAction = ({ type, payload }) =>
  ['HANDLE_LOGIN_FULFILLED', 'FETCH_CREDENTIALS_FULFILLED'].includes(type) && payload.data.type === 'customer';

export function callDurringUserAuth(durringCallSaga) {
  return fork(callDurringUserAuthSaga, durringCallSaga);
}

function* callDurringUserAuthSaga(durringCallSaga) {
  yield takeEvery([loginSuccess, authSuccess], callUntilAuthChangeSaga, durringCallSaga);
  const authId = yield select(selectors.getId);
  if (authId) {
    yield call(callUntilAuthChangeSaga, durringCallSaga);
  }
}

function* callUntilAuthChangeSaga(durringCallSaga) {
  logger.debug('Watch durring user auth');
  yield race({
    watch: call(durringCallSaga),
    cancel: take([logoutSuccess, loginSuccess, authSuccess]),
  });

  logger.debug('Stop watching durring user auth');
}

function* waitForAuthSaga() {
  const authId = yield select(selectors.getId);
  if (!authId) {
    logger.debug('User not yet authenticated - wait for user data');
    yield take([loginSuccess, authSuccess]);
  }
}

export function waitForAuth() {
  return call(waitForAuthSaga);
}

export function ensureAuthNotPending() {
  return call(function* ensureAuthNotPendingSaga() {
    const isLoginPending = yield select(selectors.isLoginPending);
    if (isLoginPending) {
      yield take([loginSuccess, authSuccess, loginRejected, authRejected]);
    }
    const isLogoutPending = yield select(selectors.isLogoutPending);
    if (isLogoutPending) {
      yield take([logoutSuccess, logoutRejected]);
    }
  });
}

export function callForEmployee(saga, ...args) {
  return call(function* forEmployeeSaga() {
    try {
      const isEmployee = yield select(selectors.isEmployee);
      if (!isEmployee) {
        return;
      }
      yield call(saga, ...args);
    } catch (e) {
      logger.error(e);
    }
  });
}

export function callForCustomer(saga, ...args) {
  return call(function* forCustomerSaga() {
    try {
      const isEmployee = yield select(selectors.isEmployee);
      if (isEmployee) {
        return;
      }
      yield call(saga, ...args);
    } catch (e) {
      logger.error(e);
    }
  });
}

export function* runWhileCustomer(saga, ...args) {
  try {
    yield fork(runWhileAnonymous, saga, ...args);
    yield fork(runWhileAuthCustomer, saga, ...args);
  } catch (e) {
    logger.error(e);
  }
}

export function* runWhileAnonymous(saga, ...args) {
  try {
    const isAuth = yield select(selectors.isAuthenticated);

    if (!isAuth) {
      yield race({
        watch: call(saga),
        cancel: take([loginSuccess, authSuccess]),
      });
    }

    yield takeEvery(logoutSuccess, function*() {
      yield race({
        watch: call(saga, ...args),
        cancel: take(logoutSuccess),
      });
    });
  } catch (e) {
    logger.error(e);
  }
}

export function* runWhileAuthCustomer(saga, ...args) {
  try {
    const isCustomer = yield select(selectors.isCustomer);

    if (isCustomer) {
      yield race({
        watch: call(saga),
        cancel: take(logoutSuccess),
      });
    }

    yield takeEvery(loginAsCustomerAction, function*() {
      yield race({
        watch: call(saga, ...args),
        cancel: take(logoutSuccess),
      });
    });
  } catch (e) {
    logger.error(e);
  }
}

export function* runWhileEmployee(saga, ...args) {
  try {
    const isEmployee = yield select(selectors.isEmployee);

    if (isEmployee) {
      yield race({
        watch: call(saga),
        cancel: take(logoutSuccess),
      });
    }

    yield takeEvery(loginAsEmployeeAction, function*() {
      yield race({
        watch: call(saga, ...args),
        cancel: take(logoutSuccess),
      });
    });
  } catch (e) {
    logger.error(e);
  }
}
